// covered-by: frontends/carrie/src/ask-v2/routes/__tests__/authLayout.test.tsx
// eslint-disable-next-line import/named
import { createRoute, redirect, useRouter } from "@tanstack/react-router";

import PendingComponent from "../../components/PendingComponent";
import { SIDEBAR_STORAGE_NAME } from "../../components/sidebar/SidebarProvider";
import { addPasswordRoute } from "../../routes/add-password/route";
import { shouldRedirectToAddPassword } from "../../routes/auth-layout/onboardingGuard";
import { requireFlagsLoaded } from "../../routes/auth-layout/requireFlagsLoaded";
import { loginRoute } from "../../routes/login/route";
import { rootRoute } from "../../routes/root/route";
import { getGuidelines } from "../../services/brand-guidelines";
import { getEnterprises } from "../../services/enterprises";
import { useAppSelector } from "../../utils/hooks";
import { logoutUrl } from "../../utils/logoutUrl";
import { queryClient } from "../../utils/queryClient";
import { setDrawerOpen } from "../../utils/slices/ui";
import { store } from "../../utils/store";
import { AskV2Layout } from "../shell/AskV2Layout";

// why: reading storage throws SecurityError rather than returning null when it is
// blocked (private mode, third-party-cookie blocking, sandboxed iframe). beforeLoad
// runs on every route resolution, so an unguarded read white-screens the route
// instead of falling back to the default drawer preference. Mirrors the branch route.
function readLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Pathless authenticated layout for Ask V2. Reuses login, not PunttLayout —
 * the tickets sidebar must not appear on ask.puntt.ai.
 */
export const askV2AuthLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "ask-v2-authenticated",
  async beforeLoad({ location }) {
    const { value: user } = store.getState().auth;
    if (user == null) {
      throw redirect({
        to: loginRoute.to,
        search: {
          redirect: location.pathname,
        },
      });
    }

    // why: invite mail now lands on this host, and without the app-host gate an
    // invited session browses ask.puntt.ai with no password. Scope is wider than
    // invitees on purpose — this mirrors the app host's auth-layout gate, so ANY
    // authenticated session whose JWT still reads `isEnterpriseOnboarded !== true`
    // (no `referredBy`, no `onboarding_flow` marker, role not CATALYST_REQUESTER —
    // see routes/auth-layout/onboardingGuard.ts) is redirected to /onboarding from
    // every ask.puntt.ai authenticated route, including a profile not yet flipped
    // to ONBOARDED. That is what the ask-host leg of
    // qa/rickrubin/tests/specs/cca/onboarding.guard.spec.ts pins: the invite link
    // lands on the password form instead of an empty shell.
    //
    // Behavior impact: an ask-host authenticated session that has not completed
    // onboarding — JWT `isEnterpriseOnboarded !== true`, and none of the three
    // onboardingGuard.ts exemptions that carry over from the app host apply (no
    // `referredBy`, no `onboarding_flow` marker, role not CATALYST_REQUESTER) —
    // is redirected from EVERY Ask route to /onboarding, mirroring the app
    // host's gate. So it is not only invitees: any passwordless session can no
    // longer browse ask.puntt.ai at all. This is a third user-visible change
    // alongside the invite-link host swap and registering /onboarding on the
    // ask route tree. The companion half ships in
    // routes/add-password/view.tsx: once the password is set it completes the
    // enterprise profile server-side (POST /private/profile/onboard) and
    // adopts the refreshed token, so a finished invitee reads onboarded on
    // BOTH the ask and app hosts and this gate stops bouncing them back here.
    //
    // Revert-kill: dropping this block turns "sends an invited user who has
    // not set a password to /onboarding" in __tests__/authLayout.test.tsx red,
    // along with the ask-host leg of onboarding.guard.spec.ts named above.
    // Widening it instead (dropping the CATALYST_REQUESTER / onboarding_flow
    // exemptions in onboardingGuard.ts) turns the sibling spec "does not send
    // a share-link requester or an in-progress onboarding to /onboarding" red.
    if (
      shouldRedirectToAddPassword(
        user,
        !!readLocalStorage("onboarding_flow") ||
          !!readLocalStorage("impersonation_bypass"),
      )
    ) {
      throw redirect({ to: addPasswordRoute.to });
    }

    // Prime the PostHog flags AND the two query caches that child `beforeLoad`
    // guards read SYNCHRONOUSLY, before any of those guards run.
    //
    // why: TanStack runs every matched route's `beforeLoad` top-down before any
    // `loader`, so a `loader`-level prime is too late. `settingsRoutes.ts` hangs
    // `canAccessAskPuntt()` / `isMeaningfulGigsOrDemo()` / `canAccessKnowledgeDatabase()`
    // off this layout, and those read `queryClient.getQueryData(["brand-guidelines"])`
    // and `(["enterprises"])` with no await — a cache miss reads as `false`, not
    // "unknown". Without this prime a hard refresh or deep link into
    // ask.puntt.ai/roi, /notifications-settings or /settings/knowledge-database
    // was bounced straight back to the V2 home by `denyToAskHome()`, and
    // `AskV2Sidebar` (same sync reads, non-reactive) hid the matching Settings
    // entries for the rest of the page's life. This is the same miss the sibling
    // layout (routes/auth-layout/route.tsx) already primes against.
    //
    // flagVariant is undefined until PostHog's callback, and the KB guard treats
    // that as deny-to-home. Every entry is swallowed like the sibling layout's:
    // a throwing PostHog subscriber or a hiccup on /private/catalyst/enterprises
    // or /brand-guidelines must degrade to "read off", not take down the whole
    // authenticated shell.
    await Promise.all([
      requireFlagsLoaded().catch(() => undefined),
      queryClient
        .ensureQueryData({
          queryKey: ["enterprises"],
          queryFn: getEnterprises,
        })
        .catch(() => undefined),
      queryClient
        .ensureQueryData({
          queryKey: ["brand-guidelines"],
          queryFn: getGuidelines,
        })
        .catch(() => undefined),
    ]);

    store.dispatch(
      setDrawerOpen({
        app:
          (readLocalStorage(`app_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
        comments:
          (readLocalStorage(`comments_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
      }),
    );
  },
  component: AskV2AuthLayout,
  pendingComponent: PendingComponent,
});

export function AskV2AuthLayout() {
  const router = useRouter();
  const user = useAppSelector((state) => state.auth.value);

  // why: beforeLoad only runs on navigation — Account → Log out clears Redux
  // without rematching routes, so the layout must hard-redirect like classic AuthLayout
  if (user == null && router.state.location.pathname !== loginRoute.to) {
    location.assign(logoutUrl(router.basepath));
    return null;
  }

  return <AskV2Layout />;
}
